import {
  BackOfficeScriptAssetType,
  ElementType,
} from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import type {
  ComponentSource,
  SiteComponentPreset,
  SiteComponentPresets,
  SiteComponentResources,
  SiteComponentElements,
} from '@wix/cli-app-manifest';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { match } from 'variant';
import path, { dirname, join } from 'node:path';
import { pathToFileURL } from 'node:url';
import type {
  SiteComponentElementItem,
  SiteComponentPresetDefinition,
  SupportedServicePlugin,
} from '@wix/cli-app-definitions';
import {
  ELEMENTS_DIR,
  getAppBackendBuildFolder,
  PRESETS_DIR,
} from '@wix/cli-app-definitions';
import type * as GetRegisteredExtensions from '@wix/cli-cloudflare-runtime/getRegisteredExtensions';
import { generateUrl } from '@wix/cli-app-manifest';
import { getBackendEventSlug } from '@wix/extensions-data-client';
import {
  virtualModuleIdForScriptAsset,
  virtualPathForHTMLAsset,
} from '../dev-center/components/dashboard/config.js';
import {
  virtualModuleIdForBlocksComponentScriptAsset,
  virtualModuleIdForCustomElementScriptAsset,
  virtualModuleIdForSiteComponentScriptAsset,
  virtualPathForCustomElementPanelHTMLAsset,
  virtualModuleIdForSiteComponentSdkScriptAsset,
  virtualModuleIdForSiteComponentPanelScriptAsset,
  virtualPresetIdForSiteComponent,
  virtualPresetIdForSiteComponentElement,
  virtualModuleIdForSiteComponentPreview,
} from '../dev-center/components/site/config.js';
import type { ProjectModel } from '../model.js';
import type { ViteBuildOutput } from './build-output-data-extractor.js';
import {
  extractBundleAssetFileName,
  extractModuleBundleContent,
  extractModuleBundleName,
  extractModuleBundleCss,
  extractPresetCss,
} from './build-output-data-extractor.js';
import type { DevCenterComponent } from '../dev-center/components/dev-center-component.js';
import { parseImageField } from './create.js';
import { SITE_COMPONENTS_BASE } from '../vite/configs/dev-command/site-components.js';

type BackendSource =
  | {
      backendBuildOutput: ViteBuildOutput | undefined;
      backendDevServerUrl?: never;
    }
  | { backendDevServerUrl: string | undefined; backendBuildOutput?: never };

interface SiteComponentSource {
  resources: SiteComponentResources;
  presets: SiteComponentPresets;
  elements: SiteComponentElements;
}

interface DevSource {
  devServerUrl: string;
}

interface BrowserBuildSource {
  browserBuildOutput: ViteBuildOutput;
}

interface WorkerBuildSource {
  workerBuildOutput?: ViteBuildOutput;
}

interface SiteComponentsClientBuildSource {
  siteComponentsClientBuildOutput?: ViteBuildOutput;
}

interface SiteComponentsSdkBuildSource {
  siteComponentsSdkBuildOutput?: ViteBuildOutput;
}

interface SiteComponentsServerBuildSource {
  siteComponentsServerBuildOutput?: ViteBuildOutput;
}

type BrowserComponentSource =
  | { devSource: DevSource; buildSource?: never }
  | { buildSource: BrowserBuildSource; devSource?: never };

type WorkerComponentSource =
  | { devSource: DevSource; buildSource?: never }
  | { buildSource: WorkerBuildSource; devSource?: never };

type DashboardSource =
  | {
      iframeUrl: string;
      scriptAsset?: never;
    }
  | {
      scriptAsset: ComponentSource['scriptAsset'];
      iframeUrl?: never;
    };

interface EmbeddedScriptSource {
  template: string;
}

interface CustomElementSource {
  scriptUrl: string;
  settingsUrl?: string;
}

interface SitePluginSource {
  scriptUrl: string;
  settingsUrl: string;
}

interface EventSource {
  webhookSlug: string;
}

interface ServicePluginSource {
  servicePluginType: SupportedServicePlugin;
}

interface BlocksComponentSource {
  componentUrl: string;
}

interface SiteComponentPanelSource {
  bundleUrl: string;
  cssUrl?: string;
}

export type ComponentSourceBuilder = <T extends DevCenterComponent>(
  component: T
) => Promise<
  T extends DevCenterComponent<'EmbeddedScript'>
    ? EmbeddedScriptSource
    : T extends DevCenterComponent<'CustomElement'>
      ? CustomElementSource
      : T extends DevCenterComponent<'DashboardPage'>
        ? DashboardSource
        : T extends DevCenterComponent<'DashboardPlugin'>
          ? DashboardSource
          : T extends DevCenterComponent<'DashboardModal'>
            ? DashboardSource
            : T extends DevCenterComponent<'Event'>
              ? EventSource[]
              : T extends DevCenterComponent<'ServicePlugin'>
                ? ServicePluginSource
                : T extends DevCenterComponent<'SitePlugin'>
                  ? SitePluginSource
                  : T extends DevCenterComponent<'BlocksWidget'>
                    ? BlocksComponentSource
                    : T extends DevCenterComponent<'BlocksWidgetPanel'>
                      ? BlocksComponentSource
                      : T extends DevCenterComponent<'SiteComponent'>
                        ? SiteComponentSource
                        : T extends DevCenterComponent<'SiteComponentPanel'>
                          ? SiteComponentPanelSource
                          : undefined
>;

async function getEmbeddedScriptDevTemplate(
  component: DevCenterComponent<'EmbeddedScript'>,
  backendDevServerUrl: string
) {
  const url = new URL(component.relativePath, backendDevServerUrl).href;
  const devTemplate = await fetch(url).then((res) => res.text());

  return devTemplate;
}

async function getEventDevSlugs(
  component: DevCenterComponent<'Event'>,
  backendDevServerUrl: string
) {
  const url = new URL(`__get-registered-events`, backendDevServerUrl).href;
  const response = await fetch(url);

  if (!response.ok) {
    const errData = await response
      .json<{ type: string; filename: string; error: string }>()
      .catch(() => null);
    if (errData?.type === 'BACKEND_EVAL_ERROR') {
      throw new CliError({
        code: CliErrorCode.FailedToEvalBackendExtension({
          filename: errData.filename,
          errorMessage: errData.error,
        }),
        cause: undefined,
      });
    }
    return null;
  }

  const eventTypes: Record<string, string> = await response.json();
  const devSlugs = eventTypes[`/${component.relativePath}`] ?? [];

  return devSlugs as string[];
}

async function getRegisteredExtensions(model: ProjectModel) {
  const backendBuildFolder = getAppBackendBuildFolder(model.projectFolder);
  const outputFilepath = join(
    backendBuildFolder,
    'getRegisteredExtensions.mjs'
  );

  return import(pathToFileURL(outputFilepath).href) as Promise<
    typeof GetRegisteredExtensions
  >;
}

async function getEventProdSlugs(
  component: DevCenterComponent<'Event'>,
  model: ProjectModel
) {
  const { getRegisteredEvents } = await getRegisteredExtensions(model);
  const { events, evaluationError } = await getRegisteredEvents();

  if (evaluationError) {
    throw new CliError({
      code: CliErrorCode.FailedToEvalBackendExtension({
        filename: evaluationError.filename,
        errorMessage: evaluationError.error,
      }),
      cause: undefined,
    });
  }

  return events[`/${component.relativePath}`];
}

async function getServicePluginDevType(
  component: DevCenterComponent<'ServicePlugin'>,
  backendDevServerUrl: string
) {
  const url = new URL(`__get-registered-service-plugins`, backendDevServerUrl)
    .href;
  const response = await fetch(url);

  if (!response.ok) {
    const errData = await response
      .json<{ type: string; filename: string; error: string }>()
      .catch(() => null);
    if (errData?.type === 'BACKEND_EVAL_ERROR') {
      throw new CliError({
        code: CliErrorCode.FailedToEvalBackendExtension({
          filename: errData.filename,
          errorMessage: errData.error,
        }),
        cause: undefined,
      });
    }
    return null;
  }

  const servicePluginTypes: Record<string, string> = await response.json();
  const servicePluginType = servicePluginTypes[`/${component.relativePath}`];

  return servicePluginType!;
}

async function getServicePluginProdType(
  component: DevCenterComponent<'ServicePlugin'>,
  model: ProjectModel
) {
  const { getRegisteredServicePlugins } = await getRegisteredExtensions(model);
  const { servicePlugins, evaluationError } =
    await getRegisteredServicePlugins();

  if (evaluationError) {
    throw new CliError({
      code: CliErrorCode.FailedToEvalBackendExtension({
        filename: evaluationError.filename,
        errorMessage: evaluationError.error,
      }),
      cause: undefined,
    });
  }

  return servicePlugins[`/${component.relativePath}`];
}

const dashboardSourceBuilder = (
  component:
    | DevCenterComponent<'DashboardPage'>
    | DevCenterComponent<'DashboardPlugin'>
    | DevCenterComponent<'DashboardModal'>,
  model: ProjectModel,
  source: BrowserComponentSource
): DashboardSource => {
  if (!model.config.trusted) {
    const fileName = virtualPathForHTMLAsset(component.id);
    return {
      iframeUrl: generateUrl(
        source.buildSource
          ? extractBundleAssetFileName(
              source.buildSource.browserBuildOutput,
              fileName
            )
          : fileName
      ),
    };
  }
  const moduleId = virtualModuleIdForScriptAsset(component.id);
  const componentUrl = generateUrl(
    source.buildSource?.browserBuildOutput
      ? extractModuleBundleName(source.buildSource.browserBuildOutput, moduleId)
      : `/@id/${moduleId}?import`
  );

  return {
    scriptAsset: {
      exportedName: component.id,
      containerId: model.config.appId,
      url: componentUrl,
      scriptType: BackOfficeScriptAssetType.MODULE,
    },
  };
};

const embeddedScriptBuilder = async (
  model: ProjectModel,
  component: DevCenterComponent<'EmbeddedScript'>,
  source: BrowserComponentSource
): Promise<EmbeddedScriptSource> => {
  return {
    template: source.buildSource
      ? extractModuleBundleContent(
          source.buildSource.browserBuildOutput,
          path.resolve(model.projectFolder, component.relativePath)
        )
      : await getEmbeddedScriptDevTemplate(
          component,
          source.devSource.devServerUrl
        ),
  };
};

const eventSourceBuilder = async (
  component: DevCenterComponent<'Event'>,
  model: ProjectModel,
  backendSource: BackendSource
): Promise<EventSource[]> => {
  const fullSlugNames = backendSource.backendDevServerUrl
    ? await getEventDevSlugs(component, backendSource.backendDevServerUrl)
    : await getEventProdSlugs(component, model);

  const webhookSlugs = fullSlugNames
    ? await Promise.all(fullSlugNames.map(getBackendEventSlug))
    : [];

  return webhookSlugs.map((webhookSlug) => ({
    webhookSlug,
  }));
};

const servicePluginSourceBuilder = async (
  component: DevCenterComponent<'ServicePlugin'>,
  model: ProjectModel,
  backendSource: BackendSource
): Promise<ServicePluginSource> => {
  const servicePluginType = backendSource.backendDevServerUrl
    ? await getServicePluginDevType(
        component,
        backendSource.backendDevServerUrl
      )
    : await getServicePluginProdType(component, model);

  if (!servicePluginType) {
    throw new Error('could not find service plugin definition');
  }

  return {
    servicePluginType,
  } as ServicePluginSource;
};

const customElementBuilder = (
  component: DevCenterComponent<'CustomElement' | 'SitePlugin'>,
  source: BrowserComponentSource
): CustomElementSource => {
  const moduleId = virtualModuleIdForCustomElementScriptAsset(component.id);
  const componentUrl = generateUrl(
    source.buildSource?.browserBuildOutput
      ? extractModuleBundleName(source.buildSource.browserBuildOutput, moduleId)
      : `/@id/${moduleId}?import`
  );

  if (component.relativePanelPath) {
    const fileName = virtualPathForCustomElementPanelHTMLAsset(component.id);
    const settingsUrl = generateUrl(
      source.buildSource
        ? extractBundleAssetFileName(
            source.buildSource.browserBuildOutput,
            fileName
          )
        : fileName
    );

    return {
      scriptUrl: componentUrl,
      settingsUrl,
    };
  }

  return {
    scriptUrl: componentUrl,
  };
};

const blocksWidgetBuilder = (
  component: DevCenterComponent<'BlocksWidget'>,
  source: WorkerComponentSource
): BlocksComponentSource => {
  const moduleId = virtualModuleIdForBlocksComponentScriptAsset(component.id);
  const componentUrl = generateUrl(
    source.buildSource?.workerBuildOutput
      ? extractModuleBundleName(source.buildSource.workerBuildOutput, moduleId)
      : `/@id/${moduleId}?import`
  );

  return {
    componentUrl,
  };
};

const blocksPanelBuilder = (
  component: DevCenterComponent<'BlocksWidgetPanel'>,
  source: WorkerComponentSource
): BlocksComponentSource => {
  const moduleId = virtualModuleIdForBlocksComponentScriptAsset(component.id);
  const componentUrl = generateUrl(
    source.buildSource?.workerBuildOutput
      ? extractModuleBundleName(source.buildSource.workerBuildOutput, moduleId)
      : `/@id/${moduleId}?import`
  );

  return {
    componentUrl,
  };
};

function traverseElementsWithPresetFn(
  elements: Record<string, SiteComponentElementItem>,
  getPresetDataFn: (
    elementsStructure: string[],
    presetName: string,
    presetConfig: SiteComponentPresetDefinition
  ) => SiteComponentPreset,
  previousElementsStructure: string[] = []
): SiteComponentElements {
  const elementsWithPresetsData: Record<string, SiteComponentElementItem> = {};

  for (const [elementName, elementItem] of Object.entries(elements)) {
    const elementsStructure = [...previousElementsStructure, elementName];
    if (elementItem.elementType === ElementType.inlineElement) {
      const presetsWithCssUrls: SiteComponentPresets = {};
      if (elementItem.inlineElement.presets) {
        for (const [presetName, presetConfig] of Object.entries(
          elementItem.inlineElement.presets
        )) {
          const presetData = getPresetDataFn(
            elementsStructure,
            presetName,
            presetConfig
          );

          presetsWithCssUrls[presetName] = {
            ...elementItem.inlineElement.presets[presetName]!,
            ...presetData,
          };
        }
      }
      const nestedElements = elementItem.inlineElement.elements
        ? traverseElementsWithPresetFn(
            elementItem.inlineElement.elements,
            getPresetDataFn,
            elementsStructure
          )
        : undefined;

      elementsWithPresetsData[elementName] = {
        ...elementItem,
        inlineElement: {
          ...elementItem.inlineElement,
          ...(Object.keys(presetsWithCssUrls).length > 0
            ? { presets: presetsWithCssUrls }
            : {}),
          ...(nestedElements ? { elements: nestedElements } : {}),
        },
      };
    }

    if (elementItem.elementType === ElementType.refElement) {
      const resolvedElement = elementItem.refElement.resolvedElement;
      if (resolvedElement) {
        const presetsWithCssUrls: SiteComponentPresets = {};

        if (resolvedElement.presets) {
          for (const [presetName, presetConfig] of Object.entries(
            resolvedElement.presets
          )) {
            const presetData = getPresetDataFn(
              elementsStructure,
              presetName,
              presetConfig
            );

            presetsWithCssUrls[presetName] = {
              ...resolvedElement.presets[presetName]!,
              ...presetData,
            };
          }
        }
        const nestedElements = resolvedElement.elements
          ? traverseElementsWithPresetFn(
              resolvedElement.elements,
              getPresetDataFn,
              elementsStructure
            )
          : undefined;

        elementsWithPresetsData[elementName] = {
          ...elementItem,
          refElement: {
            ...elementItem.refElement,
            resolvedElement: {
              ...resolvedElement,
              ...(Object.keys(presetsWithCssUrls).length > 0
                ? { presets: presetsWithCssUrls }
                : {}),
              ...(nestedElements ? { elements: nestedElements } : {}),
            },
          },
        };
      } else {
        elementsWithPresetsData[elementName] = { ...elementItem };
      }
    }
  }

  return elementsWithPresetsData as SiteComponentElements;
}

const siteComponentDevBuilder = (
  component: DevCenterComponent<'SiteComponent'>
): SiteComponentSource => {
  const moduleId = virtualModuleIdForSiteComponentScriptAsset(component.id);
  const sdkModuleId = component.relativeSdkPath
    ? virtualModuleIdForSiteComponentSdkScriptAsset(component.id)
    : null;
  const previewModuleId = component.relativePreviewPath
    ? virtualModuleIdForSiteComponentPreview(component.id)
    : null;

  const elements = traverseElementsWithPresetFn(
    component.elements,
    (elementsStructure, presetName, presetConfig) => {
      const elementPath = join(
        elementsStructure.reduce(
          (acc, elementName) => join(acc, ELEMENTS_DIR, elementName),
          ''
        )
      );

      const presetCssUrl = generateUrl(
        join(
          dirname(component.relativePath),
          elementPath,
          PRESETS_DIR,
          `${presetName}.css`
        )
      );

      return {
        ...presetConfig,
        ...(presetConfig.thumbnailUrl
          ? {
              thumbnailUrl: parseImageField(
                presetConfig.thumbnailUrl,
                elementPath
              ),
            }
          : {}),
        cssUrl: presetCssUrl,
        presetCssUrl,
      };
    }
  );

  const presets: SiteComponentPresets = component.presets
    ? Object.entries(component.presets).reduce<SiteComponentPresets>(
        (acc, [presetName, presetConfig]) => {
          const presetCssUrl = generateUrl(
            join(
              dirname(component.relativePath),
              PRESETS_DIR,
              `${presetName}.css`
            )
          );

          acc[presetName] = {
            ...presetConfig,
            thumbnailUrl:
              presetConfig.thumbnailUrl &&
              parseImageField(
                presetConfig.thumbnailUrl,
                dirname(component.relativePath)
              ),
            cssUrl: presetCssUrl,
            presetCssUrl,
          };

          return acc;
        },
        {}
      )
    : {};

  const definedResources = component.resources ?? {};

  return {
    presets,
    elements,
    resources: {
      ...definedResources,
      client: {
        ...(definedResources.client ?? {}),
        componentUrl: generateUrl(
          `${SITE_COMPONENTS_BASE}/@id/${moduleId}?import`
        ),
      },
      sdk: sdkModuleId
        ? {
            ...(definedResources.sdk ?? {}),
            url: generateUrl(
              `${SITE_COMPONENTS_BASE}/@id/${sdkModuleId}?import`
            ),
          }
        : undefined,
      server: undefined,
      editor: previewModuleId
        ? {
            ...(definedResources.editor ?? {}),
            componentUrl: generateUrl(
              `${SITE_COMPONENTS_BASE}/@id/${previewModuleId}?import`
            ),
          }
        : undefined,
    },
  };
};

const siteComponentBuildBuilder = (
  component: DevCenterComponent<'SiteComponent'>,
  { browserBuildOutput }: BrowserBuildSource,
  { siteComponentsClientBuildOutput }: SiteComponentsClientBuildSource,
  { siteComponentsServerBuildOutput }: SiteComponentsServerBuildSource,
  { siteComponentsSdkBuildOutput }: SiteComponentsSdkBuildSource
): SiteComponentSource => {
  const moduleId = virtualModuleIdForSiteComponentScriptAsset(component.id);
  const sdkModuleId = component.relativeSdkPath
    ? virtualModuleIdForSiteComponentSdkScriptAsset(component.id)
    : null;
  const previewModuleId = component.relativePreviewPath
    ? virtualModuleIdForSiteComponentPreview(component.id)
    : null;

  if (!siteComponentsClientBuildOutput || !siteComponentsServerBuildOutput) {
    throw new Error('Missing build output for site component');
  }

  const presets: SiteComponentPresets = {};

  if (component.presets) {
    for (const [presetName, presetConfig] of Object.entries(
      component.presets
    )) {
      const modulePresetId = virtualPresetIdForSiteComponent(
        component.id,
        presetName
      );

      const presetCssUrl = generateUrl(
        extractPresetCss(browserBuildOutput, modulePresetId)
      );

      presets[presetName] = {
        ...presetConfig,
        cssUrl: presetCssUrl,
        presetCssUrl,
      };
    }
  }

  const elements = traverseElementsWithPresetFn(
    component.elements,
    (elementsStructure, presetName, presetConfig) => {
      const modulePresetId = virtualPresetIdForSiteComponentElement(
        component.id,
        elementsStructure,
        presetName
      );

      const presetCssUrl = generateUrl(
        extractPresetCss(browserBuildOutput, modulePresetId)
      );

      return {
        ...presetConfig,
        cssUrl: presetCssUrl,
        presetCssUrl,
      };
    }
  );

  const definedResources = component.resources ?? {};
  const cssUrl = extractModuleBundleCss(
    siteComponentsServerBuildOutput,
    moduleId
  );
  const getSdkResource = () => {
    if (!sdkModuleId) {
      return;
    }

    if (!siteComponentsSdkBuildOutput) {
      throw new Error('Missing SDK build output for site component');
    }

    return {
      ...(definedResources.sdk ?? {}),
      url: generateUrl(
        extractModuleBundleName(siteComponentsSdkBuildOutput, sdkModuleId)
      ),
    };
  };
  const getEditorResource = () => {
    if (!previewModuleId) {
      return;
    }

    const editorCssUrl = extractModuleBundleCss(
      siteComponentsServerBuildOutput,
      previewModuleId
    );

    return {
      ...(definedResources.editor ?? {}),
      componentUrl: generateUrl(
        extractModuleBundleName(
          siteComponentsClientBuildOutput,
          previewModuleId
        )
      ),
      cssUrl: editorCssUrl ? generateUrl(editorCssUrl) : undefined,
    };
  };

  return {
    presets,
    elements,
    resources: {
      ...definedResources,
      client: {
        ...(definedResources.client ?? {}),
        componentUrl: generateUrl(
          extractModuleBundleName(siteComponentsClientBuildOutput, moduleId)
        ),
        cssUrl: cssUrl ? generateUrl(cssUrl) : undefined,
      },
      server: {
        ...(definedResources.server ?? {}),
        url: generateUrl(
          extractModuleBundleName(siteComponentsServerBuildOutput, moduleId)
        ),
      },
      sdk: getSdkResource(),
      editor: getEditorResource(),
    },
  };
};

const siteComponentPanelBuilder = (
  component: DevCenterComponent<'SiteComponentPanel'>,
  source: BrowserComponentSource
): SiteComponentPanelSource => {
  const moduleId = virtualModuleIdForSiteComponentPanelScriptAsset(
    component.id
  );
  const bundleUrl = generateUrl(
    source.buildSource?.browserBuildOutput
      ? extractModuleBundleName(source.buildSource.browserBuildOutput, moduleId)
      : `/@id/${moduleId}?import`
  );
  const cssUrl = source.buildSource?.browserBuildOutput
    ? extractModuleBundleCss(source.buildSource.browserBuildOutput, moduleId)
    : undefined;

  return {
    bundleUrl,
    cssUrl: cssUrl ? generateUrl(cssUrl) : undefined,
  };
};

export function getComponentDevSourceBuilder(
  model: ProjectModel,
  devSource: DevSource,
  backendSource: BackendSource
) {
  return (async (component: DevCenterComponent) => {
    const source = match(component, {
      EmbeddedScript: (embeddedScriptComponent) =>
        embeddedScriptBuilder(model, embeddedScriptComponent, { devSource }),
      DashboardSidebarCategory: () => {},
      CustomElement: (customElementComponent) =>
        customElementBuilder(customElementComponent, { devSource }),
      SitePlugin: (component) => customElementBuilder(component, { devSource }),
      BlocksWidget: (blocksWidgetComponent) =>
        blocksWidgetBuilder(blocksWidgetComponent, { devSource }),
      BlocksMainComponent: () => {},
      BlocksWidgetPanel: (blocksWidgetPanelComponent) =>
        blocksPanelBuilder(blocksWidgetPanelComponent, { devSource }),
      DashboardPage: (dashboardPageComponent) =>
        dashboardSourceBuilder(dashboardPageComponent, model, { devSource }),
      DashboardPlugin: (dashboardPluginComponent) =>
        dashboardSourceBuilder(dashboardPluginComponent, model, { devSource }),
      DashboardModal: (dashboardModalComponent) =>
        dashboardSourceBuilder(dashboardModalComponent, model, { devSource }),
      DashboardMenuPlugin: () => {},
      Topology: () => {},
      Api: () => {},
      WebMethod: () => {},
      Event: (component) => eventSourceBuilder(component, model, backendSource),
      ServicePlugin: (component) =>
        servicePluginSourceBuilder(component, model, backendSource),
      Monitoring: () => {},
      SiteComponent: (component) => siteComponentDevBuilder(component),
      SiteComponentPanel: (component) =>
        siteComponentPanelBuilder(component, { devSource }),
    });

    return source;
  }) as ComponentSourceBuilder;
}

export function getComponentBuildSourceBuilder(
  model: ProjectModel,
  browserBuildSource: BrowserBuildSource,
  workerBuildSource: WorkerBuildSource,
  backendSource: BackendSource,
  siteComponentsClientBuildSource: SiteComponentsClientBuildSource,
  siteComponentsServerBuildSource: SiteComponentsServerBuildSource,
  siteComponentsSdkBuildSource: SiteComponentsSdkBuildSource
) {
  return (async (component: DevCenterComponent) => {
    const source = match(component, {
      EmbeddedScript: (embeddedScriptComponent) =>
        embeddedScriptBuilder(model, embeddedScriptComponent, {
          buildSource: browserBuildSource,
        }),
      DashboardSidebarCategory: () => {},
      CustomElement: (customElementComponent) =>
        customElementBuilder(customElementComponent, {
          buildSource: browserBuildSource,
        }),
      SitePlugin: (component) =>
        customElementBuilder(component, {
          buildSource: browserBuildSource,
        }),
      BlocksWidget: (blocksWidgetComponent) =>
        blocksWidgetBuilder(blocksWidgetComponent, {
          buildSource: workerBuildSource,
        }),
      BlocksMainComponent: () => {},
      BlocksWidgetPanel: (blocksWidgetPanelComponent) =>
        blocksPanelBuilder(blocksWidgetPanelComponent, {
          buildSource: workerBuildSource,
        }),
      DashboardPage: (dashboardPageComponent) =>
        dashboardSourceBuilder(dashboardPageComponent, model, {
          buildSource: browserBuildSource,
        }),
      DashboardPlugin: (dashboardPluginComponent) =>
        dashboardSourceBuilder(dashboardPluginComponent, model, {
          buildSource: browserBuildSource,
        }),
      DashboardModal: (dashboardModalComponent) =>
        dashboardSourceBuilder(dashboardModalComponent, model, {
          buildSource: browserBuildSource,
        }),
      DashboardMenuPlugin: () => {},
      Topology: () => {},
      Api: () => {},
      WebMethod: () => {},
      Event: (component) => eventSourceBuilder(component, model, backendSource),
      ServicePlugin: (component) =>
        servicePluginSourceBuilder(component, model, backendSource),
      Monitoring: () => {},
      SiteComponent: (component) =>
        siteComponentBuildBuilder(
          component,
          browserBuildSource,
          siteComponentsClientBuildSource,
          siteComponentsServerBuildSource,
          siteComponentsSdkBuildSource
        ),
      SiteComponentPanel: (component) =>
        siteComponentPanelBuilder(component, {
          buildSource: browserBuildSource,
        }),
    });

    return source;
  }) as ComponentSourceBuilder;
}
